/**
 * Map Type Enum
 *
 * Defines valid map type.
 * Prevents magic strings and ensures type consistency across the feature.
 * Uses singleton pattern for instance management.
 *
 * @module nativeInteractiveSiteMap/enums/mapTypes
 */

export default class MapTypes {
  static instances = {};

  /**
   * Roadmap view - displays road and streets.
   */
  static get Roadmap() {
    return this.getInstance('roadmap');
  }

  /**
   * Satellite view - displays satellite imagery.
   */
  static get Satellite() {
    return this.getInstance('satellite');
  }

  /**
   * Terrain view - displays topographical features.
   */
  static get Terrain() {
    return this.getInstance('terrain');
  }

  /**
   * Hybrid view - combines satellite imagery with road overlays.
   */
  static get Hybrid() {
    return this.getInstance('hybrid');
  }

  /**
   * Get map type instance by string key.
   * @param {string} key - One of: 'roadmap', 'satellite', 'terrain', 'hybrid'
   * @returns {MapTypes} Map type instance or null if key not found
   */
  static fromKey(key) {
    const map = {
      roadmap: MapTypes.Roadmap,
      satellite: MapTypes.Satellite,
      terrain: MapTypes.Terrain,
      hybrid: MapTypes.Hybrid,
    };
    return map[key] || null;
  }

  /**
   * Get or create singleton instance for a map type.
   * @private
   * @param {string} key - Map type key
   * @returns {MapTypes} Singleton instance
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new MapTypes(key);
    }
    return this.instances[key];
  }

  /**
   * Constructor for map type instance.
   * @param {string} name - The map type name
   */
  constructor(name) {
    this.name = name;
  }

  /**
   * Get string representation of map type.
   * @returns {string} The map type name
   */
  toString() {
    return this.name;
  }
}